import { Loader2 } from 'lucide-react'
import type { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form'

import { Input } from '@/components/ui/input'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useCepLookup } from '@/hooks/use-cep-lookup'

interface AddressFormFieldsProps<T extends FieldValues> {
  form: UseFormReturn<T>
  prefix?: string
}

export function AddressFormFields<T extends FieldValues>({
  form,
  prefix,
}: AddressFormFieldsProps<T>) {
  const { lookup, isLoading: isCepLoading } = useCepLookup()

  const fieldName = (name: string) => (prefix ? `${prefix}.${name}` : name) as Path<T>

  async function handleCepBlur(cep: string) {
    const address = await lookup(cep)
    if (!address) return
    const opts = { shouldValidate: true, shouldDirty: true } as const
    form.setValue(fieldName('street'), address.street as PathValue<T, Path<T>>, opts)
    form.setValue(fieldName('neighborhood'), address.neighborhood as PathValue<T, Path<T>>, opts)
    form.setValue(fieldName('city'), address.city as PathValue<T, Path<T>>, opts)
    form.setValue(fieldName('state'), address.state as PathValue<T, Path<T>>, opts)
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={fieldName('zipCode')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>CEP</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    placeholder="00000-000"
                    {...field}
                    onBlur={(e) => {
                      field.onBlur()
                      handleCepBlur(e.target.value)
                    }}
                  />
                  {isCepLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <FormField
          control={form.control}
          name={fieldName('street')}
          render={({ field }) => (
            <FormItem className="col-span-8">
              <FormLabel>Rua / Logradouro</FormLabel>
              <FormControl>
                <Input placeholder="Av. Paulista" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={fieldName('number')}
          render={({ field }) => (
            <FormItem className="col-span-4">
              <FormLabel>Número</FormLabel>
              <FormControl>
                <Input placeholder="1000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <FormField
          control={form.control}
          name={fieldName('complement')}
          render={({ field }) => (
            <FormItem className="col-span-6">
              <FormLabel>Complemento</FormLabel>
              <FormControl>
                <Input placeholder="Sala 40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={fieldName('neighborhood')}
          render={({ field }) => (
            <FormItem className="col-span-6">
              <FormLabel>Bairro</FormLabel>
              <FormControl>
                <Input placeholder="Bela Vista" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <FormField
          control={form.control}
          name={fieldName('city')}
          render={({ field }) => (
            <FormItem className="col-span-8">
              <FormLabel>Cidade</FormLabel>
              <FormControl>
                <Input placeholder="São Paulo" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={fieldName('state')}
          render={({ field }) => (
            <FormItem className="col-span-4">
              <FormLabel>UF</FormLabel>
              <FormControl>
                <Input placeholder="SP" maxLength={2} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
